import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Check,
  X,
  Edit,
  Trash2,
  Filter,
  Search,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Info,
  Replace,
  Loader2,
  FileText,
  LayoutTemplate,
  Layers,
  List
} from 'lucide-react';
import MuscleBadges from '../common/MuscleBadges';
import ExerciseForm from '../exercises/ExerciseForm';
import { clusterExercisesBySimilarName, findSimilarInList, exerciseNameSimilarity } from '../../utils/exerciseNameSimilarity';

const ExerciseApprovalPanel = ({ onApprovalAction }) => {
  const { getAuthenticatedApi } = useAuth();
  const { t } = useTranslation();
  const api = getAuthenticatedApi();
  
  const [pendingExercises, setPendingExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('pending');
  const [originFilter, setOriginFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingExercise, setEditingExercise] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [message, setMessage] = useState(null);
  const [replacingExerciseId, setReplacingExerciseId] = useState(null);
  const [replaceSearchQuery, setReplaceSearchQuery] = useState('');
  const [replaceExercises, setReplaceExercises] = useState([]);
  const [replaceLoading, setReplaceLoading] = useState(false);
  const [usageByExerciseId, setUsageByExerciseId] = useState({});
  const [usageDialogExercise, setUsageDialogExercise] = useState(null);
  const [groupDuplicates, setGroupDuplicates] = useState(true);

  useEffect(() => {
    fetchPendingExercises();
  }, []);

  // Carica utilizzo (schede/template) per tutti gli esercizi della lista
  const fetchUsageForExercises = async (exercises) => {
    if (!exercises?.length) return;
    try {
      const ids = exercises.map(ex => ex.id).join(',');
      const response = await api.get('/exercise_usage_api.php', { params: { exercise_ids: ids } });
      if (response.data?.success && response.data?.usage) {
        setUsageByExerciseId(response.data.usage);
      }
    } catch (err) {
      console.error('Errore caricamento utilizzo esercizi:', err);
    }
  };

  const fetchPendingExercises = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/pending_exercises.php');
      
      if (response.data.success) {
        const exercises = response.data.exercises || [];
        setPendingExercises(exercises);
        fetchUsageForExercises(exercises);
      } else {
        setError(response.data.message || 'Errore durante il recupero degli esercizi');
      }
    } catch (err) {
      console.error('Errore durante il recupero degli esercizi:', err);
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveExercise = async (id) => {
    try {
      setLoading(true);
      
      const response = await api.post('/approve_exercise.php', {
        exercise_id: id,
        action: 'approve'
      });
      
      if (response.data.success) {
        setPendingExercises(prevExercises =>
          prevExercises.map(ex =>
            ex.id === id ? { ...ex, status: 'approved', created_by_user_id: null } : ex
          )
        );
        
        setMessage({
          type: 'success',
          text: 'Esercizio approvato con successo!'
        });
        
        setTimeout(() => setMessage(null), 3000);
        
        if (onApprovalAction) onApprovalAction();
      } else {
        setError(response.data.message || 'Errore durante l\'approvazione');
      }
    } catch (err) {
      console.error('Errore:', err);
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectExercise = async (id) => {
    try {
      setLoading(true);
      
      const response = await api.post('/approve_exercise.php', {
        exercise_id: id,
        action: 'reject'
      });
      
      if (response.data.success) {
        setPendingExercises(prevExercises => 
          prevExercises.map(ex => 
            ex.id === id ? { ...ex, status: 'user_only' } : ex
          )
        );
        
        setMessage({
          type: 'success',
          text: 'Esercizio rifiutato!'
        });
        
        setTimeout(() => setMessage(null), 3000);
        
        if (onApprovalAction) onApprovalAction();
      } else {
        setError(response.data.message || 'Errore durante il rifiuto');
      }
    } catch (err) {
      console.error('Errore:', err);
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExercise = async (id) => {
    try {
      setLoading(true);
      
      // CORREZIONE: Utilizzo dell'endpoint esercizi.php invece di exercise.php
      const response = await api.delete(`/esercizi.php?id=${id}`);
      
      if (response.data && response.data.message) {
        setPendingExercises(prevExercises => 
          prevExercises.filter(ex => ex.id !== id)
        );
        
        setConfirmDeleteId(null);
        
        setMessage({
          type: 'success',
          text: 'Esercizio eliminato con successo!'
        });
        
        setTimeout(() => setMessage(null), 3000);
        
        if (onApprovalAction) onApprovalAction();
      } else {
        setError(response.data?.message || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Errore:', err);
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per cercare esercizi esistenti per la sostituzione
  const searchReplaceExercises = async (query) => {
    if (!query || query.trim() === '') {
      setReplaceExercises([]);
      return;
    }

    try {
      setReplaceLoading(true);
      const response = await api.get('/esercizi.php', {
        params: {
          search: query.trim(),
          limit: 20,
          include_muscles: true
        }
      });
      
      // Filtra solo esercizi approvati (status = 'approved' o null)
      const approvedExercises = Array.isArray(response.data) 
        ? response.data.filter(ex => !ex.status || ex.status === 'approved')
        : [];
      
      setReplaceExercises(approvedExercises);
    } catch (err) {
      console.error('Errore nella ricerca esercizi:', err);
      setReplaceExercises([]);
    } finally {
      setReplaceLoading(false);
    }
  };

  // Handler per aprire il modal di sostituzione
  const handleOpenReplaceModal = (exerciseId) => {
    setReplacingExerciseId(exerciseId);
    setReplaceSearchQuery('');
    setReplaceExercises([]);
  };

  // Handler per chiudere il modal di sostituzione
  const handleCloseReplaceModal = () => {
    setReplacingExerciseId(null);
    setReplaceSearchQuery('');
    setReplaceExercises([]);
  };

  // Handler per la sostituzione dell'esercizio
  const handleReplaceExercise = async (replacementExerciseId) => {
    if (!replacingExerciseId || !replacementExerciseId) {
      setError('ID esercizio non valido');
      return;
    }

    try {
      setLoading(true);
      
      const response = await api.post('/approve_exercise.php', {
        exercise_id: replacingExerciseId,
        action: 'replace',
        replacement_exercise_id: replacementExerciseId
      });
      
      if (response.data.success) {
        // Rimuovi l'esercizio sostituito dalla lista
        setPendingExercises(prevExercises => 
          prevExercises.filter(ex => ex.id !== replacingExerciseId)
        );
        
        setMessage({
          type: 'success',
          text: response.data.message || 'Esercizio sostituito con successo!'
        });
        
        setTimeout(() => setMessage(null), 5000);
        
        handleCloseReplaceModal();
        
        if (onApprovalAction) onApprovalAction();
      } else {
        setError(response.data.message || 'Errore durante la sostituzione');
      }
    } catch (err) {
      console.error('Errore:', err);
      setError(err.response?.data?.message || 'Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  // Debounce per la ricerca esercizi
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (replacingExerciseId) {
        searchReplaceExercises(replaceSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [replaceSearchQuery, replacingExerciseId]);


  const filteredExercises = useMemo(() => {
    return pendingExercises.filter((ex) => {
      const statusMatch =
        filterType === 'all' ||
        (filterType === 'pending' && ex.status === 'pending_review') ||
        (filterType === 'approved' && ex.status === 'approved') ||
        (filterType === 'rejected' && ex.status === 'user_only');

      const q = searchQuery.trim().toLowerCase();
      const searchMatch =
        !q ||
        (ex.nome || '').toLowerCase().includes(q) ||
        (ex.gruppo_muscolare || '').toLowerCase().includes(q) ||
        String(ex.id).includes(q) ||
        (ex.user_username || '').toLowerCase().includes(q) ||
        (ex.user_name || '').toLowerCase().includes(q) ||
        (ex.creator_gym_name || '').toLowerCase().includes(q);

      const originMatch = originFilter === 'all' || ex.creator_kind === originFilter;

      return statusMatch && searchMatch && originMatch;
    });
  }, [pendingExercises, filterType, searchQuery, originFilter]);

  const displayGroups = useMemo(() => {
    if (!groupDuplicates || filteredExercises.length < 2) {
      return filteredExercises.map((ex) => ({
        key: `single-${ex.id}`,
        label: ex.nome,
        exercises: [ex],
        isDuplicateGroup: false
      }));
    }
    return clusterExercisesBySimilarName(filteredExercises);
  }, [filteredExercises, groupDuplicates]);

  const duplicateGroupCount = useMemo(
    () => displayGroups.filter((g) => g.isDuplicateGroup).length,
    [displayGroups]
  );

  const creatorKindLabel = (kind) => {
    const key = `admin.exerciseApproval.origin.${kind || 'unknown'}`;
    const label = t(key);
    return label === key ? t('admin.exerciseApproval.origin.unknown') : label;
  };

  const creatorKindClass = (kind) => {
    switch (kind) {
      case 'gym':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200';
      case 'trainer':
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200';
      case 'standalone':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200';
      case 'gym_member':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const renderCreator = (exercise) => {
    const kind = exercise.creator_kind || 'unknown';
    const who = exercise.user_name || exercise.user_username || `#${exercise.created_by_user_id}`;
    return (
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${creatorKindClass(kind)}`}>
          {creatorKindLabel(kind)}
        </span>
        <span>
          {t('admin.exerciseApproval.createdBy')}{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">{who}</span>
          {exercise.user_username && exercise.user_name ? (
            <span className="text-gray-400"> @{exercise.user_username}</span>
          ) : null}
        </span>
        {exercise.creator_gym_name ? (
          <span>
            · {t('admin.exerciseApproval.creatorGym')}:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">{exercise.creator_gym_name}</span>
          </span>
        ) : null}
        {exercise.attrezzatura ? (
          <span>
            · {t('admin.exerciseApproval.equipment')}: {exercise.attrezzatura}
          </span>
        ) : null}
      </p>
    );
  };

  const renderStatusBadge = (exercise) => {
    if (exercise.status === 'pending_review') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5" />
          {t('admin.exerciseApproval.statusPending')}
        </span>
      );
    }
    if (exercise.status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
          <CheckCircle className="h-3.5 w-3.5" />
          {t('admin.exerciseApproval.statusApproved')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
        <XCircle className="h-3.5 w-3.5" />
        {t('admin.exerciseApproval.statusRejected')}
      </span>
    );
  };

  const renderUsageChips = (exercise) => {
    const usage = usageByExerciseId[String(exercise.id)];
    const schede = usage?.schede || [];
    const countT = (usage?.count_templates ?? 0) + (usage?.count_templates_standalone ?? 0);
    const maxSchedeShown = 3;

    return (
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {schede.length === 0 && countT === 0 ? (
          <span className="text-xs text-gray-500 dark:text-gray-400 italic">
            {t('admin.exerciseApproval.notUsedYet')}
          </span>
        ) : (
          <>
            {schede.slice(0, maxSchedeShown).map((s) => (
              <span
                key={`${exercise.id}-scheda-${s.id}`}
                className="inline-flex items-center gap-1 max-w-[220px] text-xs px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200 border border-indigo-100 dark:border-indigo-800"
                title={s.assignee ? `${s.nome} - ${s.assignee}` : s.nome}
              >
                <FileText className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{s.nome}</span>
                {s.assignee && <span className="text-indigo-500 dark:text-indigo-300 truncate">· {s.assignee}</span>}
              </span>
            ))}
            {schede.length > maxSchedeShown && (
              <button
                type="button"
                onClick={() => setUsageDialogExercise(exercise)}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                +{schede.length - maxSchedeShown} {t('admin.exerciseApproval.moreSheets')}
              </button>
            )}
            {countT > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                <LayoutTemplate className="h-3 w-3" />
                {t('admin.exerciseApproval.templatesCount', { count: countT })}
              </span>
            )}
          </>
        )}
        {(schede.length > 0 || countT > 0) && (
          <button
            type="button"
            onClick={() => setUsageDialogExercise(exercise)}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-indigo-600 transition-colors"
            title={t('admin.exerciseApproval.usageDetails')}
          >
            <Info className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const renderSimilarAlert = (exercise, peersInGroup) => {
    const similar =
      peersInGroup?.length > 0
        ? peersInGroup
        : findSimilarInList(exercise, filteredExercises);
    if (!similar.length) return null;

    return (
      <div className="mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <p className="text-xs font-medium text-amber-900 dark:text-amber-100 mb-1.5">
          {t('admin.exerciseApproval.similarTitle')}
        </p>
        <ul className="space-y-1">
          {similar.map((other) => (
            <li key={other.id} className="text-xs text-amber-800 dark:text-amber-200 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="font-medium">{other.nome}</span>
              <span className="text-amber-700/80 dark:text-amber-300/80">#{other.id}</span>
              <span>· {Math.round(exerciseNameSimilarity(exercise.nome, other.nome) * 100)}%</span>
              {other.status === 'approved' && (
                <span className="text-emerald-700 dark:text-emerald-300">({t('admin.exerciseApproval.alreadyApproved')})</span>
              )}
              {exercise.status === 'pending_review' && (
                <button
                  type="button"
                  onClick={() => handleOpenReplaceModal(exercise.id)}
                  className="text-indigo-700 dark:text-indigo-300 underline hover:no-underline"
                >
                  {t('admin.exerciseApproval.mergeViaReplace')}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderExerciseCard = (exercise, peersInGroup = []) => (
    <div
      key={exercise.id}
      className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm"
    >
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{exercise.nome}</h3>
              <span className="text-xs font-mono text-gray-400 dark:text-gray-500">#{exercise.id}</span>
              {renderStatusBadge(exercise)}
              {exercise.is_cardio ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200">
                  Cardio
                </span>
              ) : null}
            </div>
            <MuscleBadges
              primaryMuscle={exercise.primary_muscle}
              secondaryMuscles={exercise.secondary_muscles}
              variant="compact"
            />
            {renderCreator(exercise)}
            {renderUsageChips(exercise)}
            {renderSimilarAlert(exercise, peersInGroup)}
          </div>

          <div className="flex flex-wrap gap-2 flex-shrink-0">
            {exercise.status === 'pending_review' && (
              <>
                <button
                  type="button"
                  onClick={() => handleApproveExercise(exercise.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg"
                  title={t('admin.exerciseApproval.approve')}
                >
                  <Check className="h-4 w-4" />
                  {t('admin.exerciseApproval.approve')}
                </button>
                <button
                  type="button"
                  onClick={() => handleOpenReplaceModal(exercise.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-violet-600 hover:bg-violet-700 rounded-lg"
                  title={t('admin.exerciseApproval.replace')}
                >
                  <Replace className="h-4 w-4" />
                  {t('admin.exerciseApproval.replace')}
                </button>
                <button
                  type="button"
                  onClick={() => handleRejectExercise(exercise.id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
                  title={t('admin.exerciseApproval.reject')}
                >
                  <X className="h-4 w-4" />
                  {t('admin.exerciseApproval.reject')}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setEditingExercise(exercise)}
              className="p-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
              title={t('admin.exerciseApproval.edit')}
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeleteId(exercise.id)}
              className="p-2 text-white bg-gray-600 hover:bg-gray-700 rounded-lg"
              title={t('admin.exerciseApproval.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {exercise.descrizione && (
        <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
          {exercise.descrizione}
        </div>
      )}

      {confirmDeleteId === exercise.id && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">{t('admin.exerciseApproval.confirmDelete')}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmDeleteId(null)}
              className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg"
            >
              {t('admin.exerciseApproval.cancel')}
            </button>
            <button
              type="button"
              onClick={() => handleDeleteExercise(exercise.id)}
              className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg"
            >
              {t('admin.exerciseApproval.confirmDeleteBtn')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('admin.exerciseApproval.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.exerciseApproval.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchPendingExercises}
          className="flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800/50 transition"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('admin.exerciseApproval.refresh')}
        </button>
      </div>

      <div className="p-4 mb-6 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 rounded-lg flex items-start gap-2 border border-amber-200 dark:border-amber-800">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <p className="text-sm">{t('admin.exerciseApproval.duplicateHint')}</p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <p>{message.text}</p>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg">
          <p>{error}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.exerciseApproval.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        
        <div className="relative">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none w-full md:w-auto"
          >
            <option value="all">{t('admin.exerciseApproval.filterAll')}</option>
            <option value="pending">{t('admin.exerciseApproval.filterPending')}</option>
            <option value="approved">{t('admin.exerciseApproval.filterApproved')}</option>
            <option value="rejected">{t('admin.exerciseApproval.filterRejected')}</option>
          </select>
          <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>

        <div className="relative">
          <select
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
            className="pl-4 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none w-full md:w-auto"
          >
            <option value="all">{t('admin.exerciseApproval.originFilterAll')}</option>
            <option value="gym">{t('admin.exerciseApproval.origin.gym')}</option>
            <option value="trainer">{t('admin.exerciseApproval.origin.trainer')}</option>
            <option value="standalone">{t('admin.exerciseApproval.origin.standalone')}</option>
            <option value="gym_member">{t('admin.exerciseApproval.origin.gym_member')}</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-sm text-gray-600 dark:text-gray-400 mr-1">{t('admin.exerciseApproval.view')}:</span>
        <button
          type="button"
          onClick={() => setGroupDuplicates(false)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${
            !groupDuplicates
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600'
          }`}
        >
          <List className="h-4 w-4" />
          {t('admin.exerciseApproval.viewList')}
        </button>
        <button
          type="button"
          onClick={() => setGroupDuplicates(true)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${
            groupDuplicates
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600'
          }`}
        >
          <Layers className="h-4 w-4" />
          {t('admin.exerciseApproval.viewGrouped')}
        </button>
        {groupDuplicates && duplicateGroupCount > 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            {t('admin.exerciseApproval.duplicateGroups', { count: duplicateGroupCount })}
          </span>
        )}
      </div>

      {loading && pendingExercises.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredExercises.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          {searchQuery || filterType !== 'all' || originFilter !== 'all'
            ? t('admin.exerciseApproval.emptyFiltered')
            : t('admin.exerciseApproval.empty')}
        </div>
      ) : (
        <div className="space-y-6">
          {displayGroups.map((group) => (
            <div key={group.key}>
              {group.isDuplicateGroup && (
                <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <Layers className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                      {t('admin.exerciseApproval.groupTitle', { count: group.exercises.length })}
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
                      {t('admin.exerciseApproval.groupHint')}
                    </p>
                  </div>
                </div>
              )}
              <div className={`space-y-3 ${group.isDuplicateGroup ? 'pl-0 sm:pl-2 border-l-2 border-amber-300 dark:border-amber-700' : ''}`}>
                {group.exercises.map((exercise) =>
                  renderExerciseCard(
                    exercise,
                    group.isDuplicateGroup
                      ? group.exercises.filter((e) => e.id !== exercise.id)
                      : []
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modal per la modifica dell'esercizio */}
      {editingExercise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <ExerciseForm
              exercise={editingExercise}
              onExerciseAdded={(updatedExercise) => {
                setEditingExercise(null);
                fetchPendingExercises();
                if (onApprovalAction) {
                  onApprovalAction('updated', updatedExercise || editingExercise);
                }
              }}
              onClose={() => setEditingExercise(null)}
            />
          </div>
        </div>
      )}

      {/* Modal "Dove è utilizzato" (info utilizzo esercizio) */}
      {usageDialogExercise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setUsageDialogExercise(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Info className="h-5 w-5 text-indigo-500" />
                Dove è utilizzato
              </h3>
              <button
                type="button"
                onClick={() => setUsageDialogExercise(null)}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {usageDialogExercise.nome}
              </p>
              {(() => {
                const usage = usageByExerciseId[String(usageDialogExercise.id)];
                if (!usage) {
                  return <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento...</p>;
                }
                const { schede, templates, templates_standalone } = usage;
                const noUsage = (!schede || schede.length === 0) && (!templates || templates.length === 0) && (!templates_standalone || templates_standalone.length === 0);
                if (noUsage) {
                  return <p className="text-sm text-gray-500 dark:text-gray-400">Non utilizzato in nessuna scheda né template.</p>;
                }
                return (
                  <div className="space-y-4">
                    {schede && schede.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4" />
                          Schede ({schede.length})
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {schede.map(s => (
                            <li key={s.id}>
                              {s.nome}
                              {s.assignee && <span className="text-gray-500 dark:text-gray-500"> - {s.assignee}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {templates && templates.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-2">
                          <LayoutTemplate className="h-4 w-4" />
                          Template palestra ({templates.length})
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {templates.map(t => (
                            <li key={t.id}>
                              {t.name}
                              {t.gym_name && <span className="text-gray-500 dark:text-gray-500"> - {t.gym_name}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {templates_standalone && templates_standalone.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-2">
                          <LayoutTemplate className="h-4 w-4" />
                          Template standalone ({templates_standalone.length})
                        </h4>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {templates_standalone.map(t => (
                            <li key={t.id}>{t.name}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal per la sostituzione dell'esercizio */}
      {replacingExerciseId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Sostituisci Esercizio
                </h3>
                <button
                  onClick={handleCloseReplaceModal}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Cerca e seleziona un esercizio esistente per sostituire questo esercizio. 
                L'esercizio originale verrà eliminato e tutte le schede e template che lo utilizzano verranno aggiornati.
              </p>
            </div>
            
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={replaceSearchQuery}
                  onChange={(e) => setReplaceSearchQuery(e.target.value)}
                  placeholder="Cerca esercizio per nome..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
              </div>

              {replaceLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : replaceExercises.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {replaceSearchQuery
                    ? 'Nessun esercizio trovato. Prova con un altro termine di ricerca.'
                    : 'Inizia a digitare per cercare un esercizio...'}
                </div>
              ) : (
                <div className="space-y-2">
                  {replaceExercises.map((exercise) => {
                    // Costruisci URL immagine usando solo immagine_nome
                    const imageUrl = exercise.immagine_nome
                      ? `/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`
                      : null;
                    
                    return (
                      <div
                        key={exercise.id}
                        onClick={() => handleReplaceExercise(exercise.id)}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                      >
                        <div className="flex justify-between items-start gap-4">
                          {/* Miniatura immagine */}
                          {imageUrl && (
                            <div className="flex-shrink-0">
                              <img
                                src={imageUrl}
                                alt={exercise.nome}
                                className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {exercise.nome}
                            </h4>
                            {exercise.descrizione && (
                              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                {exercise.descrizione}
                              </p>
                            )}
                            <div className="mt-2">
                              <MuscleBadges
                                primaryMuscle={exercise.primary_muscle}
                                secondaryMuscles={exercise.secondary_muscles}
                                variant="compact"
                              />
                            </div>
                            {exercise.attrezzatura && (
                              <span className="mt-2 inline-block text-xs text-gray-500 dark:text-gray-400">
                                {exercise.attrezzatura}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplaceExercise(exercise.id);
                            }}
                            className="ml-4 flex-shrink-0 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
                          >
                            Sostituisci
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseApprovalPanel;